import axios from 'axios';
import { useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAppSelector } from '../../hooks';
import { appSelectors } from '../../redux/appSlice';

interface FormValues {
  full_name: string;
  email: string;
  consent: boolean;
  newsletter: boolean;
}

export const RegisterForm = () => {
  const [showForm, setShowForm] = useState<boolean>(true);
  const [token, setToken] = useState<string | null>(null);
  const { register, formState, handleSubmit } = useForm<FormValues>();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const csrfToken = useAppSelector(appSelectors.csrfToken);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const recaptchaValue = recaptchaRef.current?.getValue();
    if (!recaptchaValue) {
      return;
    }

    const formResponse = await axios.post(
      `${import.meta.env.VITE_WEBFORM_URL}/submit`,
      {
        webform_id: 'user_registration',
        ...data,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
      }
    );
    if (formResponse.status === 200) {
      setShowForm(false);
    }
  };

  if (!showForm) {
    return (
      <div className="register-form-content">
        <p>
          Thank you for requesting a user account. We will be in touch with you
          shortly.
        </p>
        <p>
          <strong>
            Please note, all of these requests are processed manually.
            Therefore, it may be a few days until we are in touch with your
            login details. Thank you for your patience.
          </strong>
        </p>
      </div>
    );
  }

  return (
    <div className="register-form-content">
      <div className="register-intro">
        <p>Please complete the form below to request a user account.</p>
        <p>
          Having an image library account means that you can create your own
          collections and you don't need to input your details whenever
          downloading an image. Please complete the form below to request a user
          account.
        </p>
        <p>
          To find out how we'll look after your details, please read our{' '}
          <a
            className="privacy-policy-link"
            href="/privacy-policy"
            target="_blank"
          >
            privacy policy
          </a>
          .
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="input-wrapper">
          <label htmlFor="full_name">Full name:</label>
          <input
            {...register('full_name', { required: 'Full Name required' })}
            type="text"
            id="full_name"
          />
        </div>
        <div className="input-wrapper">
          <label>Email address:</label>
          <input
            {...register('email', {
              required: 'Email address is required',
              pattern: {
                value:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: 'Please enter a valid email',
              },
            })}
            type="email"
            required
          />
        </div>
        <div className="input-wrapper-checkbox">
          <input
            {...register('consent', { required: 'Consent required' })}
            type="checkbox"
            id="consent_check"
          />
          <label htmlFor="consent_check">
            I consent to the personal data I have provided above being processed
            for the purposes of accessing this system. This processing includes
            logging my activity and contacting me as necessary for the use of
            the system.
          </label>
        </div>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
          onChange={(token) => setToken(token)}
        />
        <div className="input-wrapper-checkbox">
          <input
            {...register('newsletter')}
            type="checkbox"
            id="newsletter_check"
          />
          <label htmlFor="newsletter_check">
            I would like to receive emails from Centre for Ageing Better about
            the image library
          </label>
        </div>
        {formState.errors.email?.message && (
          <p className="error">{formState.errors.email?.message}</p>
        )}
        {formState.errors.consent?.message && (
          <p className="error">{formState.errors.consent?.message}</p>
        )}
        <button type="submit" className="submit-button" disabled={!token}>
          Request user login
        </button>
      </form>
      <p>
        ** We will safeguard your personal details and will not sell to other
        organisations. You can change your preferences at any time by contacting
        us at digital@ageing-better.org.uk.
      </p>
    </div>
  );
};
